import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import SingleVariationField from './SingleVariationField';
interface AddNewVariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentID: any;
  count: number;
  setCount: any;
}

interface VariationData {
  variationid: number;
  documentid: number;
  name: string;
  description: string;
  fields: any;
}

function AddNewVariationModal({
  isOpen,
  onClose,
  documentID,
  count,
  setCount,
}: AddNewVariationModalProps) {
  const [addVariationData, setAddVariationData] = useState<VariationData>({
    variationid: 0,
    documentid: documentID,
    name: '',
    description: '',
    fields: [],
  });
  const [dropDownOptions, setDropDownOptions] = useState();
  let toast = useToast();

  useEffect(() => {
    const baseurl = process.env.REACT_APP_SERVER_SCENARIOS_BASE_URL as string;
    const tokenUrl = process.env.REACT_APP_SCENARIO_TOKEN_URL as string;

    // Function to fetch the token and then the forms
    let fetchForms = async () => {
      try {
        let tokenResponse = await axios.get(tokenUrl);

        if (tokenResponse.data.success) {
          let formsResponse = await axios.get(
            baseurl + '/Appeal/get_available_fields',
            {
              headers: {
                Pragma: 'no-cache',
                Authorization: 'Bearer ' + tokenResponse.data.token,
              },
            }
          );
          if (formsResponse.status === 200) {
            setDropDownOptions(formsResponse.data);
          } else {
            throw new Error(
              `Failed to fetch forms: ${
                formsResponse.data.message || 'Unknown error'
              }`
            );
          }
        } else {
          throw new Error(
            `Failed to retrieve auth token: ${
              tokenResponse.data.message || 'Unknown error'
            }`
          );
        }
      } catch (error: any) {
        console.error(
          'Error fetching forms:',
          error.response ? error.response.data : error.message
        );
      }
    };

    fetchForms();
  }, [addVariationData]);

  useEffect(() => {
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;
    const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;

    // Function to fetch the token and then the forms
    const fetchForms = async () => {
      try {
        const tokenResponse = await axios.get(tokenUrl);

        if (tokenResponse.data.success) {
          const formsResponse = await axios.get(
            baseurl +
              '/App/api/get_document_variations?documentid=' +
              documentID,
            {
              headers: {
                Pragma: 'no-cache',
                Authorization: 'Bearer ' + tokenResponse.data.token,
              },
            }
          );

          if (formsResponse.data.success) {
            const variations = formsResponse.data.fields;
            setAddVariationData(prevData => ({
              ...prevData,
              fields: variations,
            }));
          } else {
            throw new Error('Failed to fetch forms');
          }
        } else {
          throw new Error('Failed to retrieve auth token');
        }
      } catch (error) {
        console.error('Error fetching forms:', error);
      }
    };

    fetchForms();
  }, []);

  const handleChangeDefaultValue = (fieldId: number, value: string) => {
    const updatedFields = addVariationData.fields.map((fields: any) => {
      if (fields.fieldid === fieldId) {
        return { ...fields, defaultvalue: `FIELD:${value}` };
      }
      return fields;
    });

    setAddVariationData(prevState => ({
      ...prevState,
      fields: updatedFields,
    }));
  };

  const addVariation = async () => {
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;
    const tokenUrl = process.env.REACT_APP_SCENARIO_TOKEN_URL as string;
    try {
      const tokenResponse = await axios.get(tokenUrl);
      if (tokenResponse.data.success) {
        const formsResponse = await axios.post(
          baseurl + '/App/api/save_variation',
          {
            name: addVariationData.name,
            description: addVariationData.description,
            documentid: addVariationData.documentid,
            variationid: addVariationData.variationid,
            fields: addVariationData.fields,
          },

          {
            headers: {
              Pragma: 'no-cache',
              Authorization: 'Bearer ' + tokenResponse.data.token,
            },
          }
        );
        if (formsResponse.data.success) {
          toast({
            title: 'Success',
            position: 'top',
            description: 'Variation Successfully Added!',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          setCount(count + 1);
        } else {
          throw new Error('Failed to upload data');
        }
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error Uploading data:', error);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault(); // Prevent the default form submission behavior
    addVariation();
    onClose();
    setAddVariationData((prevFormData: any) => ({
      ...prevFormData,
      variationid: 0,
      documentid: documentID,
      name: '',
      description: '',
      fields: [],
    }));
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setAddVariationData((prevFormData: any) => ({
            ...prevFormData,
            variationid: 0,
            documentid: documentID,
            name: '',
            description: '',
            fields: [],
          }));
        }}
        size="6xl"
      >
        <ModalOverlay
          onClick={() => {
            onClose();
            setAddVariationData((prevFormData: any) => ({
              ...prevFormData,
              variationid: 0,
              documentid: documentID,
              name: '',
              description: '',
              fields: [],
            }));
          }}
        />
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader textAlign="center">Add New Variation</ModalHeader>
            <ModalCloseButton color="white" bg=" #002677" />
            <ModalBody>
              <Flex textAlign="center" flexDir="column">
                <FormControl width="500px" isRequired>
                  <FormLabel>Name:</FormLabel>
                  <Input
                    type="text"
                    placeholder="Insert Name"
                    value={addVariationData.name}
                    onChange={event => {
                      const newName = event.target.value;
                      setAddVariationData((prevFormData: any) => ({
                        ...prevFormData,
                        name: newName,
                      }));
                    }}
                  />
                </FormControl>
                <FormControl width="500px" isRequired>
                  <FormLabel>Description:</FormLabel>
                  <Input
                    type="text"
                    placeholder="Insert Description"
                    onChange={event => {
                      const newDesc = event.target.value;
                      setAddVariationData((prevFormData: any) => ({
                        ...prevFormData,
                        description: newDesc,
                      }));
                    }}
                  />
                </FormControl>
                <Wrap mt="20px">
                  {addVariationData && addVariationData.fields
                    ? addVariationData.fields.map((data: any) => (
                        <WrapItem key={data.fieldid}>
                          <Box width="300px">
                            <SingleVariationField
                              fieldData={data}
                              dropDownOptions={dropDownOptions}
                              handleChangeDefaultValue={
                                handleChangeDefaultValue
                              }
                            />
                          </Box>
                        </WrapItem>
                      ))
                    : ''}
                </Wrap>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button
                bg="#002677"
                textColor="white"
                mr={3}
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Spacer />
              <Button
                variant="ghost"
                bg="#002677"
                textColor="white"
                type="submit"
              >
                Add Variation
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}

export default AddNewVariationModal;
